import { Button, Group, Stack, TextInput } from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { LabelingType } from '@/types/dataset'
import { MLModelType } from '@/types/model'
import { LabelingTypeCardSelect } from '../LabelingTypeCardSelect/LabelingTypeCardSelect'

export type FormValues = {
  name: string
  labeling_type: LabelingType
}

type CreateModelFormProps = {
  modelType: MLModelType
  onCancel: () => void
  onSubmit: (formValues: FormValues) => void
}

export const CreateModelForm = ({
  modelType,
  onCancel,
  onSubmit
}: CreateModelFormProps) => {
  const intl = useIntl()

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      labeling_type: LabelingType.ObjectDetection
    },

    validate: {
      name: isNotEmpty(intl.formatMessage({ id: 'models.enterModelName' }))
    }
  })

  return (
    <form noValidate onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Stack gap={60}>
        <TextInput
          label={<FormattedMessage id="models.modelName" />}
          autoFocus
          data-autofocus // required for auto focus inside the modal
          {...form.getInputProps('name')}
        />

        <div>
          <LabelingTypeCardSelect
            modelType={modelType}
            label={<FormattedMessage id="models.labelingType.label" />}
            description={
              <FormattedMessage id="models.labelingType.description" />
            }
            {...form.getInputProps('labeling_type')}
          />

          {form.values.labeling_type === LabelingType.Classification && (
            <PlatformAlert variant="info" mt="sm">
              <FormattedMessage id="models.classification.deviceSupport.info" />
            </PlatformAlert>
          )}
        </div>

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} type="submit">
            <FormattedMessage id="models.create" />
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
